<template>
    <div>
        <b-form-row>
            <b-col>
                <b-form-group
                    id="proposalTypeFieldset"
                    label="Category:"
                    label-cols-md="3"
                    label-align-md="right"
                >
                    <b-form-select
                        v-model="form.ref_proposal_type_id"
                        :options="selects.proposalTypes"
                        :disabled="!canSelectProposalType"
                        value-field="id"
                        text-field="description"
                        @input="clearProgramAreaSelections"
                    >
                        <template v-if="canSelectProposalType" slot="first">
                            <option :value="null" disabled>
                                -- Please select a Category --
                            </option>
                        </template>
                    </b-form-select>
                </b-form-group>
            </b-col>
        </b-form-row>

        <b-form-row>
            <b-col>
                <b-form-group id="focusAreaFieldset"
                              label="Focus Area:"
                              label-cols-md="3"
                              label-align-md="right"
                >
                    <b-form-select
                        v-model="form.focus_area"
                        :options="selects.focusArea"
                        value-field="areaName"
                        text-field="areaName"
                        :disabled="!canSelectFocusArea"
                    >
                        <template v-if="form.ref_proposal_type_id === null" slot="first">
                            <option :value="null" disabled>
                                -- Please select a Category --
                            </option>
                        </template>
                        <template v-else-if="!canSelectFocusArea" slot="first">
                            <option :value="null" disabled>
                                -- All Focus Areas --
                            </option>
                        </template>
                        <template v-else-if="canSelectFocusArea" slot="first">
                            <option :value="null" disabled>
                                -- Please select a Focus Area --
                            </option>
                        </template>
                    </b-form-select>
                </b-form-group>
            </b-col>
        </b-form-row>

        <template v-if="clusterAyCheck">
            <b-form-row>
                <b-col>
                    <b-form-group id="clustersFieldset"
                                  label="Cluster:"
                                  label-cols-md="3"
                                  label-align-md="right"
                    >
                        <b-form-select
                            id="cost_type"
                            v-model="form.cluster"
                            class="field-contents"
                            title="-- Please select an option --"
                            :options="clusterOptions"
                            :disabled="!canSelectCluster"
                        >
                            <template slot="first">
                                <b-form-select-option :value="null">
                                    All Clusters
                                </b-form-select-option>
                            </template>
                        </b-form-select>
                    </b-form-group>
                </b-col>
            </b-form-row>
        </template>

        <b-form-row>
            <b-col>
                <b-form-group id="proposalTitleFieldset"
                              label="Title:"
                              label-cols-md="3"
                              label-align-md="right"
                >
                    <div v-if="!form.proposal_title" slot="description" />
                    <b-form-input
                        v-model="form.proposal_title"
                        type="text"
                        :disabled="formDisabled"
                    />
                </b-form-group>
            </b-col>
        </b-form-row>

        <b-row v-if="!viewingProposal">
            <b-col
                v-b-tooltip.hover
                align-self="end"
                title="Please complete the 'Activity Title' field and
                make a selection in each dropdown above to enable the SAVE button."
                :disabled="hasValidFormData"
            >
                <v-button
                    id="addProposalSaveButton"
                    :loading="isSaving"
                    :variant="'outline-tetradic-3'"
                    :type="'outline-tetradic-3'"
                    :disabled="!hasValidFormData"
                    @click="saveProposalInfo"
                >
                    <fa-icon :icon="['fas', 'save']" /> Create Activity Proposal
                </v-button>
            </b-col>
        </b-row>
        <b-row v-if="viewingProposal && !formDisabled">
            <b-col align-self="end">
                <b-button
                    id="addProposalSaveButton"
                    :loading="isSaving"
                    :variant="'outline-tetradic-3'"
                    :disabled="!hasValidFormData"
                    @click="saveProposalInfo"
                >
                    <fa-icon :icon="['fas', 'save']" /> Save
                </b-button>
            </b-col>
        </b-row>
    </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import { Arc, Log } from '$Shared/@arc/vue-arc';

const emptyForm = {
    submission_type: 'new',
    funding_type: 'funding',
    ref_proposal_type_id: null,
    lea: null,
    lea_type: null,
    occupational_area: null, // obsolete
    cluster: null, // obsolete
    pathway: null, // obsolete
    program_of_study: null, // obsolete
    proposal_title: null,
    focus_area: null,
};

export default {
    name: 'AddProposal',
    props: {
        applicationId: { type: Number, required: true },
        lea: { type: String, required: true },
        viewingProposal: { type: Boolean, default: false },
        formDisabled: { type: Boolean, default: false },
    },

    data() {
        return {
            form: { ...Arc.Objects.clone(emptyForm), lea: this.lea },
            isSaving: false,
            saveButtonState: {
                type: Boolean,
                default: false,
            },
        };
    },

    computed: {
        ...mapState('ref/application', {
            localApplicationClusters: 'localApplicationClusters',
        }),
        ...mapGetters({
            proposalTypes: 'ref/application/proposalTypes',
            proposalData: 'proposal/proposalData',
            proposalId: 'proposal/proposalId',
            eeCodeType: 'ref/ee/eeCodeType',
            focusAreas: 'ref/application/focusAreas',
            pathwayClusterItems: 'ref/global/pathwayClusters',
            applicationAy: 'application/applicationAy',
        }),

        payload() {
            return {
                name: this.$options.name,
                content: {
                    ...this.form,
                    applicationId: this.applicationId,
                    lea: this.lea,
                    lea_type: this.eeCodeType(this.lea),
                    content: {
                        proposal_title: this.form.proposal_title,
                        focus_area: this.form.focus_area,
                        cluster: this.form.cluster,
                    },
                },
            };
        },

        isNewProposal() {
            return this.isInitialEmptyValue('proposal_id');
        },

        // TODO/Refactor/Extract: Extract these hard-coded numbers to Constants/Getters somewhere else
        isAllFocusAreas() {
            return (this.form.ref_proposal_type_id === 25 || this.form.ref_proposal_type_id === 23);
        },

        canSelectFocusArea() {
            return !this.formDisabled &&
                !this.isAllFocusAreas &&
                this.form.ref_proposal_type_id !== null;
        },

        canSelectProposalType() {
            return !this.formDisabled && this.isNewProposal;
        },

        canSelectCluster() {
            return !this.formDisabled;
        },


        hasType() {
            return !this.isInitialEmptyValue(['ref_proposal_type_id']);
        },
        hasTitle() {
            return !this.isInitialEmptyValue(['proposal_title']);
        },
        hasFocusArea() {
            return !this.isInitialEmptyValue(['focus_area']);
        },
        hasValidFormData() {
            return (this.isAllFocusAreas)
                ? (this.hasType && this.hasTitle)
                : (this.hasType && this.hasTitle && this.hasFocusArea);
        },

        currentAyProposalTypes() {
            let types = [];
            if (this.proposalTypes) {
                types = this.proposalTypes.map((item) => {
                    return {
                        ...item,
                        value: item.id,
                        text: item.description,
                        // disabled: !(item.id === 22 || item.id === 23),
                        disabled: false,
                    };
                });
            }
            return types;
        },

        selects() {
            return {
                focusArea: this.focusAreas || [this.proposalData.content.focus_area] || [],
                proposalTypes: this.currentAyProposalTypes,
            };
        },

        clusterItems() {
            let items = [];
            if (this.localApplicationClusters) {
                let clusters = this.localApplicationClusters.local_application_clusters;

                clusters.forEach((cluster) => {
                    this.pathwayClusterItems.forEach((item) => {
                        if (cluster.clusterId === item.cluster_id) {
                            items.push(item);
                        }
                    });
                });
            }
            return items;
        },

        clusterOptions() {
            return this.clusterItems.map((cluster) => {
                return {
                    ...cluster,
                    value: cluster.cluster_id,
                    text: cluster.cluster_name,
                };
            });
        },

        clusterAyCheck() {
            let show = false;
            if (this.applicationAy) {
                let Ay = Number(this.applicationAy);
                if (Ay >= 22) {
                    show = true;
                }
            }
            return show;
        }
    },

    watch: {
        proposalData: {
            handler(newVal) {
                if (newVal && newVal.proposal_id) {
                    // Viewing an existing proposal
                    this.form = {
                        ...newVal,
                        proposal_title: newVal.content ? newVal.content.proposal_title : null,
                        focus_area: newVal.content ? newVal.content.focus_area : null,
                        cluster: newVal.content ? newVal.content.cluster : null,
                    };
                } else {
                    // Creating a new proposal
                    this.form = { ...Arc.Objects.clone(emptyForm) };
                }
            },
            immediate: true,
        },
    },

    methods: {
        ...mapActions({
            startFullScreenLoader: 'loading/startFullScreenLoader',
            stopFullScreenLoader: 'loading/stopFullScreenLoader',
        }),

        saveProposalInfo() {
            this.startFullScreenLoader();
            this.isSaving = true;
            let action = this.proposalId === null ? 'proposal/createProposal' : 'proposal/updateProposal';

            this.$store
                .dispatch(action, { ...this.payload })
                .then((response) => {
                    this.isSaving = false;
                    Log.debug(response, 'Add a new activity: ');

                    return response;
                })
                .then((response) => {
                    if (action === 'proposal/createProposal') {
                        this.clearForm();
                        this.$store.dispatch('proposal/resetProposalData');
                    }

                    return response;
                })
                .catch((arcAlert) => {
                    return arcAlert;
                })
                .finally(() => {
                    this.isSaving = false;
                    this.stopFullScreenLoader();
                });
        },

        clearForm() {
            this.form = { ...Arc.Objects.clone(emptyForm), lea: this.lea };
            this.saveButtonState = this.hasValidFormData;
        },

        clearProgramAreaSelections() {
            this.form.occupational_area = null;
            this.form.pathway = null;
            this.form.program_of_study = null;
            this.form.focus_area = null;
            this.form.cluster = null;
        },

        isInitialEmptyValue(fields, nestedParent) {
            if (!Array.isArray(fields)) {
                fields = [fields];
            }

            return fields.some((field) => {
                return nestedParent
                    ? this.form[nestedParent][field] === emptyForm[nestedParent][field]
                    : this.form[field] === emptyForm[field];
            });
        },
    },
};
</script>

<style scoped lang="scss">
@import '$StyleVars';

.rounded-button {
    border-radius: 5px;
    color: #ffffff;
    font-weight: bold;
    background-color: $realm-tetradic-tertiary;
    border: 0;
    box-shadow: 0 2px 5px gray;

    &:hover,
    &:focus {
        background-color: darken($realm-tetradic-tertiary, 10%);
    }

    &:active {
        color: #ffffff;
        font-weight: bolder;
        border-radius: 5px 5px 0 0;

        background: rgb(109, 140, 57);
        background: linear-gradient(180deg, rgba(109, 140, 57, 1) 0%, rgba(109, 140, 57, 0.75) 50%);
    }
}
</style>
